import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Pagination, Table } from "flowbite-react";
import { useQuery, useQueryClient } from "react-query";
import moment from "moment";
import "moment-timezone";
import "moment/locale/pt-br";
import { FiEye } from "react-icons/fi";
import { FaEdit, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { HiInformationCircle } from "react-icons/hi";
import {
  MdAdd,
  MdOutlineBusinessCenter,
  MdOutlineFileDownload,
  MdOutlineManageAccounts,
} from "react-icons/md";

import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { STAFF, CLIENTS, CRM } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { useApplication } from "../../contexts/ApplicationContext";
import Modal from "../modals/Modal";
import CompanySupportForm from "../forms/CompanySupportForm";
import ProfessionalForm from "../forms/ProfessionalForm";
import CustomerKpiCards from "../customers/CustomerKpiCards";
// `Badge` do flowbite já está em uso nesta tela, então o do CRM entra
// renomeado — os dois convivem na mesma célula.
import { Badge as CrmBadge } from "../crm/Primitives";
import CustomerFiltersBar from "../customers/CustomerFiltersBar";
import {
  describeExpiration,
  describeLastActivity,
  formatDate,
  formatMoney,
  formatNumber,
  getErrorMessage,
  getHealthMeta,
  getPlanMeta,
  getStatusMeta,
} from "../customers/customerHelpers";

moment.tz.setDefault("America/Sao_Paulo");
moment.locale("pt-br");

const DEFAULT_FILTERS = {
  planId: "",
  status: "",
  health: "",
  // Estágio do ciclo de vida (CRM). Vem na URL quando a pessoa clica em "ver
  // todas na tabela" numa coluna do pipeline.
  stage: "",
  registrationStartDate: "",
  registrationEndDate: "",
  ownerUserId: "",
  sortBy: "createdAt",
  sortOrder: "desc",
  itemsPerPage: 16,
  onlyNew: false,
  onlyOverSeatLimit: false,
};

const COLUMN_COUNT = 8;

const buildQueryString = (filters, search, page) => {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(filters.itemsPerPage),
    search,
    planId: filters.planId,
    status: filters.status,
    health: filters.health,
    stage: filters.stage,
    registrationStartDate: filters.registrationStartDate,
    registrationEndDate: filters.registrationEndDate,
    ownerUserId: filters.ownerUserId,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    onlyNew: String(Boolean(filters.onlyNew)),
    onlyOverSeatLimit: String(Boolean(filters.onlyOverSeatLimit)),
  });

  return params.toString();
};

const countActiveFilters = (filters) =>
  [
    filters.planId,
    filters.status,
    filters.health,
    filters.stage,
    filters.registrationStartDate,
    filters.registrationEndDate,
    filters.ownerUserId,
    filters.onlyNew ? "1" : "",
    filters.onlyOverSeatLimit ? "1" : "",
  ].filter(Boolean).length;

const SortableHeader = ({ field, label, sortBy, sortOrder, onSort, className = "" }) => {
  const isActive = sortBy === field;
  const Icon = !isActive ? FaSort : sortOrder === "asc" ? FaSortUp : FaSortDown;

  return (
    <Table.HeadCell className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`flex items-center gap-1 whitespace-nowrap transition ${
          isActive ? "text-pink-600" : "text-slate-500 hover:text-slate-800"
        }`}
      >
        {label}
        <Icon className={isActive ? "" : "opacity-30"} size={11} />
      </button>
    </Table.HeadCell>
  );
};

const CustomersTable = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = useApplication();

  /*
    O estágio pode chegar pela URL (/clientes?stage=FREE_IDLE), que é o link
    das colunas do pipeline. Lido só na montagem: depois disso quem manda é o
    filtro da tela, e reagir à URL faria o filtro voltar sozinho ao valor
    inicial a cada troca.
  */
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    stage: searchParams.get("stage") || "",
  }));
  const [searchTerm, setSearchTerm] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [feedback, setFeedback] = useState(null);

  const [isCompanyFormOpen, setIsCompanyFormOpen] = useState(false);
  const [companyFormMode, setCompanyFormMode] = useState("EDIT");
  const [editingProfessional, setEditingProfessional] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const debouncedSearch = useDebouncedValue(searchTerm, 400);
  const queryString = buildQueryString(filters, debouncedSearch, currentPage);

  const { isLoading, isFetching, error, data } = useQuery(
    ["companies", queryString],
    async () => {
      const { data: response } = await axios.get(
        `${CLIENTS.LIST}?${queryString}`
      );

      return response.data;
    },
    { keepPreviousData: true }
  );

  // Estágios do CRM, para o filtro. Mudam muito raramente (só com deploy),
  // então ficam em cache por uma hora em vez de acompanhar o refetch global.
  const { data: stages = [] } = useQuery(
    ["crm", "stages"],
    async () => {
      const { data: response } = await axios.get(CRM.STAGES);
      return response.data;
    },
    { staleTime: 60 * 60 * 1000, refetchInterval: false }
  );

  // Lista usada tanto no filtro quanto no seletor de responsável do cadastro.
  const { data: owners = [] } = useQuery(
    ["backoffice-user-options"],
    async () => {
      const { data: response } = await axios.get(STAFF.OPTIONS);

      return response.data;
    }
  );

  const companies = useMemo(() => data?.data ?? [], [data]);
  const summary = data?.summary;
  const totalPages = data?.totalPages || 1;
  const totalCount = data?.totalCount || 0;

  // Volta para a primeira página sempre que o recorte de dados muda.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  useEffect(() => {
    if (!feedback) return undefined;

    const timeout = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(timeout);
  }, [feedback]);

  const refreshCompanies = useCallback(() => {
    queryClient.invalidateQueries("companies");
  }, [queryClient]);

  const handleFilterChange = (partial) => {
    setFilters((previous) => ({ ...previous, ...partial }));
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchTerm("");
  };

  const handleSort = (field) => {
    setFilters((previous) => ({
      ...previous,
      sortBy: field,
      sortOrder:
        previous.sortBy === field && previous.sortOrder === "desc" ? "asc" : "desc",
    }));
  };

  /*
    O olho (e o nome do cliente) agora LEVAM para a ficha completa, em vez de
    abrir um modal.

    O modal mostrava um recorte — dava para ver alguns números e nada do
    relacionamento. A ficha tem a série de uso, o histórico de contato, as
    tarefas, a cobrança e o botão de registrar pagamento. Abrir um resumo
    quando existe a página inteira obrigava a fechar o modal e navegar assim
    mesmo.
  */
  const handleViewCompany = (company) => {
    navigate(`/clientes/${company.id}`);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setCompanyFormMode("EDIT");
    setIsCompanyFormOpen(true);
  };

  const handleCreateCompany = () => {
    setSelectedCompany(null);
    setCompanyFormMode("CREATE");
    setIsCompanyFormOpen(true);
  };

  // Vem da aba "Equipe" do painel de detalhes, já com o cadastro completo.
  // O CSV já vem do servidor com BOM e separador ";", que o Excel em pt-BR
  // abre direto, sem passo de importação.
  const downloadCsv = (data, fileName) => {
    const url = window.URL.createObjectURL(
      new Blob([data], { type: "text/csv;charset=utf-8;" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await axios.get(`${CLIENTS.EXPORT}?${queryString}`, {
        responseType: "blob",
      });

      downloadCsv(
        response.data,
        `clientes-${moment().format("YYYY-MM-DD-HHmm")}.csv`
      );
      setFeedback({ type: "success", message: "Exportação gerada com sucesso." });
    } catch (exportError) {
      setFeedback({ type: "failure", message: getErrorMessage(exportError) });
    } finally {
      setIsExporting(false);
    }
  };

  /** Base de clientes finais do salão, com histórico de uso de cada um. */
  const activeFilterCount = countActiveFilters(filters);
  const hasCompanies = companies.length > 0;

  return (
    <>
      <div className="space-y-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Gestão
              </p>
              <h2 className="text-xl font-semibold text-slate-900">
                Clientes da plataforma
              </h2>
              <p className="text-sm text-slate-500">
                Situação do plano, engajamento real e ações rápidas de suporte em
                um único lugar.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                color="light"
                onClick={handleExport}
                isProcessing={isExporting}
                disabled={isExporting || !totalCount}
                title="Exportar os clientes do filtro atual em CSV"
              >
                <MdOutlineFileDownload className="mr-2 h-4 w-4" />
                Exportar CSV
              </Button>
              {isAdmin ? (
                <Button size="sm" onClick={handleCreateCompany}>
                  <MdAdd className="mr-2 h-4 w-4" />
                  Novo cliente
                </Button>
              ) : null}
            </div>
          </div>
        </section>

        {feedback ? (
          <Alert
            color={feedback.type}
            icon={HiInformationCircle}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.message}
          </Alert>
        ) : null}

        <CustomerKpiCards
          summary={summary}
          isLoading={isLoading}
          activeStatus={filters.status}
          activeHealth={filters.health}
          onSelect={handleFilterChange}
        />

        <CustomerFiltersBar
          stages={stages}
          filters={filters}
          owners={owners}
          canFilterByOwner={isAdmin}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onChange={handleFilterChange}
          onReset={handleResetFilters}
          showAdvanced={showAdvancedFilters}
          onToggleAdvanced={() => setShowAdvancedFilters((previous) => !previous)}
          activeFilterCount={activeFilterCount}
        />

        {error ? (
          <Alert color="failure" icon={HiInformationCircle}>
            Não foi possível carregar os clientes. {getErrorMessage(error)}
          </Alert>
        ) : null}

        <section className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Table hoverable>
            <Table.Head className="bg-slate-50">
              <SortableHeader
                field="name"
                label="Cliente"
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSort={handleSort}
              />
              <SortableHeader
                field="planId"
                label="Plano"
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSort={handleSort}
              />
              <SortableHeader
                field="planExpirationDate"
                label="Situação"
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSort={handleSort}
              />
              <SortableHeader
                field="appointmentsLast30Days"
                label="Engajamento"
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSort={handleSort}
                className="hidden lg:table-cell"
              />
              <SortableHeader
                field="professionalCount"
                label="Equipe"
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSort={handleSort}
                className="hidden xl:table-cell"
              />
              <Table.HeadCell className="hidden xl:table-cell">
                Responsável
              </Table.HeadCell>
              <SortableHeader
                field="totalPaid"
                label="Financeiro"
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSort={handleSort}
                className="hidden xl:table-cell"
              />
              <Table.HeadCell className="text-center">Ações</Table.HeadCell>
            </Table.Head>

            <Table.Body className="divide-y">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, rowIndex) => (
                  <Table.Row key={`skeleton-${rowIndex}`}>
                    {Array.from({ length: COLUMN_COUNT }).map((__, cellIndex) => (
                      <Table.Cell key={`skeleton-cell-${cellIndex}`}>
                        <div className="h-3 w-full animate-pulse rounded-full bg-slate-200" />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              ) : hasCompanies ? (
                companies.map((company) => {
                  const statusMeta = getStatusMeta(company.status);
                  const healthMeta = getHealthMeta(company.health);
                  const planMeta = getPlanMeta(company.planId);
                  return (
                    <Table.Row key={company.id} className="bg-white transition">
                      <Table.Cell className="max-w-[260px]">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Link
                              to={`/clientes/${company.id}`}
                              className="truncate text-sm font-semibold text-slate-900 hover:text-pink-600"
                              title={company.name}
                            >
                              {company.name}
                            </Link>
                            {company.isNew ? (
                              <Badge color="success" className="px-1.5 py-0 text-[10px]">
                                novo
                              </Badge>
                            ) : null}
                          </div>
                          <p className="truncate text-xs text-slate-500">
                            #{company.id} · {company.email || "sem e-mail"}
                          </p>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <Badge color={planMeta.color} className="w-fit text-xs">
                          {planMeta.label}
                        </Badge>
                        <span className="mt-1 block text-xs text-slate-500">
                          {formatMoney(company.monthlyValue)}/mês
                        </span>
                      </Table.Cell>

                      <Table.Cell>
                        <span className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-slate-800">
                          <span
                            className={`h-2 w-2 shrink-0 rounded-full ${statusMeta.dot}`}
                          />
                          {describeExpiration(company.daysToExpire)}
                        </span>
                        <span className="block text-[11px] text-slate-500">
                          {formatDate(company.planExpirationDate)}
                        </span>
                      </Table.Cell>

                      <Table.Cell className="hidden lg:table-cell">
                        {/*
                          Estágio do CRM ao lado do engajamento. São coisas
                          diferentes: engajamento é "usou nos últimos 30 dias",
                          estágio cruza isso com pagamento e tempo de casa —
                          um free ativo e um pagante que parou podem ter o
                          mesmo engajamento e exigir ações opostas.
                        */}
                        <div className="flex flex-wrap items-center gap-1">
                          <CrmBadge tone={company.stageTone}>
                            {company.stageLabel}
                          </CrmBadge>
                          <Badge
                            color={healthMeta.color}
                            className="w-fit text-xs"
                            title={healthMeta.hint}
                          >
                            {healthMeta.label}
                          </Badge>
                        </div>
                        <span className="mt-1 block text-xs text-slate-500">
                          {formatNumber(company.appointmentsLast30Days)} agend. /30d ·{" "}
                          {describeLastActivity(
                            company.lastActivityAt,
                            company.daysSinceLastActivity
                          )}
                        </span>
                      </Table.Cell>

                      <Table.Cell className="hidden xl:table-cell">
                        <span
                          className={`text-sm font-semibold ${
                            company.isOverSeatLimit
                              ? "text-rose-600"
                              : "text-slate-800"
                          }`}
                          title={
                            company.isOverSeatLimit
                              ? "Profissionais cadastrados acima do limite contratado"
                              : "Profissionais ativos / limite contratado"
                          }
                        >
                          {formatNumber(company.professionalCount)}/
                          {formatNumber(company.seatLimit)}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {formatNumber(company.customerCount)} clientes finais
                        </span>
                      </Table.Cell>

                      <Table.Cell className="hidden xl:table-cell">
                        {company.ownerName ? (
                          <span className="text-sm text-slate-800">
                            {company.ownerName}
                          </span>
                        ) : (
                          <span className="text-xs italic text-slate-400">
                            sem responsável
                          </span>
                        )}
                      </Table.Cell>

                      <Table.Cell className="hidden xl:table-cell">
                        <span className="text-sm font-semibold text-slate-800">
                          {formatMoney(company.totalPaid)}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {company.lastPaymentAt
                            ? `último em ${formatDate(company.lastPaymentAt)}`
                            : "sem pagamentos"}
                        </span>
                      </Table.Cell>

                      <Table.Cell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => handleViewCompany(company)}
                            title="Abrir ficha do cliente"
                          >
                            <FiEye />
                          </Button>
                          {isAdmin ? (
                            <Button
                              size="xs"
                              color="light"
                              onClick={() => handleEditCompany(company)}
                              title="Editar cliente"
                            >
                              <FaEdit />
                            </Button>
                          ) : null}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={COLUMN_COUNT} className="py-12 text-center">
                    <p className="text-sm font-medium text-slate-700">
                      Nenhum cliente encontrado.
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Ajuste a busca ou limpe os filtros para ver mais resultados.
                    </p>
                    {(activeFilterCount > 0 || searchTerm) && (
                      <Button
                        size="xs"
                        color="light"
                        className="mx-auto mt-3"
                        onClick={handleResetFilters}
                      >
                        Limpar filtros
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </section>

        <div className="flex flex-col items-center justify-between gap-3 pb-4 md:flex-row">
          <p className="text-xs text-slate-500">
            {totalCount
              ? `Mostrando ${formatNumber(data?.rangeStart)}–${formatNumber(
                  data?.rangeEnd
                )} de ${formatNumber(totalCount)} clientes`
              : "Nenhum resultado"}
            {isFetching && !isLoading ? " · atualizando…" : ""}
          </p>

          {totalPages > 1 ? (
            <Pagination
              layout="pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              previousLabel=""
              nextLabel=""
              showIcons
            />
          ) : null}
        </div>
      </div>


      <Modal
        isOpen={isCompanyFormOpen}
        onClose={setIsCompanyFormOpen}
        size="lg"
        icon={MdOutlineBusinessCenter}
        title={companyFormMode === "CREATE" ? "Novo cliente" : "Editar cliente"}
        subtitle={
          companyFormMode === "EDIT" && selectedCompany
            ? `${selectedCompany.name} · #${selectedCompany.id}`
            : "Cadastro da empresa na plataforma"
        }
        bodyClassName="bg-slate-50/50"
      >
        <CompanySupportForm
          company={selectedCompany}
          onClose={setIsCompanyFormOpen}
          isDrawerOpen={isCompanyFormOpen}
          fetchCompanies={refreshCompanies}
          owners={owners}
          canAssignOwner={isAdmin}
          type={companyFormMode}
        />
      </Modal>

      <Modal
        isOpen={Boolean(editingProfessional)}
        onClose={() => setEditingProfessional(null)}
        size="xl"
        icon={MdOutlineManageAccounts}
        title="Editar profissional"
        subtitle={editingProfessional?.name}
        bodyClassName="bg-slate-50/50"
      >
        {editingProfessional ? (
          <ProfessionalForm
            professional={editingProfessional}
            isOpen={Boolean(editingProfessional)}
            onClose={() => setEditingProfessional(null)}
            fetchProfessionals={() =>
              queryClient.invalidateQueries("companyDetails")
            }
            type="EDIT"
          />
        ) : null}
      </Modal>
    </>
  );
};

export default CustomersTable;
